import { createContext, useContext, useState, ReactNode } from 'react';
import { AlignJustify, Layers, BookOpen } from 'lucide-react';

export type ScrollMode = 'normal' | 'stack' | 'book';

interface ScrollModeValue {
  mode: ScrollMode;
  setMode: (m: ScrollMode) => void;
}

const ScrollModeContext = createContext<ScrollModeValue>({
  mode: 'normal',
  setMode: () => {},
});

export function useScrollMode() {
  return useContext(ScrollModeContext);
}

export function ScrollModeProvider({ children }: { children: ReactNode }) {
  const [mode, setMode] = useState<ScrollMode>('normal');
  return (
    <ScrollModeContext.Provider value={{ mode, setMode }}>
      {children}
    </ScrollModeContext.Provider>
  );
}

/** Classes applied to each "page" (hero + StackSection) when in book mode. */
export function bookPageClass(mode: ScrollMode) {
  return mode === 'book'
    ? 'w-screen shrink-0 snap-start h-full overflow-y-auto themed-scroll'
    : '';
}

const options: { key: ScrollMode; label: string; icon: typeof Layers }[] = [
  { key: 'normal', label: 'Normal', icon: AlignJustify },
  { key: 'stack', label: 'Stack', icon: Layers },
  { key: 'book', label: 'Book', icon: BookOpen },
];

/** Floating control to switch scroll modes. */
export function ScrollModeToggle() {
  const { mode, setMode } = useScrollMode();
  return (
    <div className="fixed bottom-4 left-1/2 z-50 flex -translate-x-1/2 items-center gap-0.5 rounded-full border border-slate-200 bg-white/90 p-0.5 shadow-[0_8px_28px_-12px_rgba(37,99,235,0.35)] backdrop-blur-xl">
      {options.map(({ key, label, icon: Icon }) => (
        <button
          key={key}
          type="button"
          onClick={() => setMode(key)}
          aria-pressed={mode === key}
          title={label}
          className={`flex items-center gap-1.5 rounded-full px-3 py-1 transition-colors ${
            mode === key
              ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white'
              : 'text-slate-500 hover:text-blue-700'
          }`}
        >
          <Icon className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">{label}</span>
        </button>
      ))}
    </div>
  );
}
